<script setup lang="ts">
import { computed, ref, watch } from "vue";

import { useCreateBuildingProject, WizardProjectType } from "@/features/BuildingProject";
import { useLocalization, Country_Enum, countryLongNameToEnumMap } from "@/providers/Localization";
import { useProjectRevisionsStore } from "@/pages/ProjectVersionHistory/stores/projectRevisions.store";

import { BuildingAddressType } from "@/features/BuildingProject";

import { BookOpen, ChevronDown, ChevronUp, MapPin } from "@lucide/vue";

import AddressInput from "@/components/Reusables/Inputs/AddressInput.vue";
import StepIntro from "../StepIntro/StepIntro.vue";
import StepCard from "../StepCard.vue";
import DE from "./Content/DE.vue";
import DK from "./Content/DK.vue";
import FI from "./Content/FI.vue";
import FO from "./Content/FO.vue";
import GL from "./Content/GL.vue";
import IE from "./Content/IE.vue";
import IS from "./Content/IS.vue";
import NO from "./Content/NO.vue";
import SE from "./Content/SE.vue";
import Fallback from "./Content/Fallback.vue";
import { useWizardProject } from "@/features/BuildingProject/composables/useWizardProject";

const props = withDefaults(
  defineProps<{
    wasValidated: boolean;
    project: WizardProjectType;
    /**
     * Whether an address is demanded before the host will accept a save.
     *
     * False by default, because the settings page is this step's only host and its Location section is
     * optional: `runSave` refuses the WHOLE save when any section is invalid, and every project the
     * start dialog creates has a country and no street, so demanding one would make the page
     * unsaveable for them. It also drives the label's asterisk, and `wasValidated` here is
     * `saveAttempted` — set on every save, not just a failed one — so without this an addressless
     * project turned red under a save that had in fact succeeded.
     *
     * A PARTIAL address still reports its missing parts either way: a place that came back without a
     * street number is a bad answer, not an absent one.
     */
    required?: boolean;
  }>(),
  { required: false }
);

const createBuildingProject = useCreateBuildingProject();
const localization = useLocalization();
const revisionsStore = useProjectRevisionsStore();

const { project, projectId, field } = useWizardProject(() => props.project);

const buildingAddress = field("buildingAddress");
const validationRules = createBuildingProject.getValidationRules();

// The wizard binds the LIVE project row, and base info is the one thing the dashboard endpoint does
// not resolve against a revision pin — so without this the field and the map keep showing today's
// address while the rest of the page describes an older save point. The revision's own row has
// it, via the project_revision.location computed field that the dashboard's Location slide reads.
//
// Applied only while PREVIEWING, the same gate as the B6 step (see OperationalEnergyUse.vue): this
// is a form, so what is on screen must be what Save writes. Preview is explicit, reversible and
// read-only — the settings page disables Save — whereas an admin merely parking the current
// pointer on an older save point leaves the whole team editing the live head, where showing a fold
// would invite writing yesterday's address back over today's.
//
// Display-only: the overlay never reaches project.buildingAddress, so nothing folded can end up in
// the object Save serialises.

const revisionLocation = computed(() =>
  projectId.value && revisionsStore.isPreviewing(projectId.value) ? revisionsStore.readLocationFor(projectId.value) : null
);

/** True while the step is showing a revision's saved address instead of the live, editable one. */
const isReadOnly = computed(() => revisionLocation.value !== null);

/** The address on screen: the revision's where it has a row of its own, the live one otherwise. */
const displayedAddress = computed<BuildingAddressType>({
  get: () => {
    const liveAddress = project.value.buildingAddress;
    const location = revisionLocation.value;
    if (!location) return liveAddress;

    return {
      street: location.street ?? liveAddress.street,
      streetNumber: location.streetNumber ?? liveAddress.streetNumber,
      postalCode: String(location.postalCode ?? liveAddress.postalCode),
      city: location.city ?? liveAddress.city,
      country: location.country ?? liveAddress.country,
    };
  },
  set: (address) => {
    if (isReadOnly.value) return;
    buildingAddress.value = address;
  },
});

const addressInputErrors = computed(() => {
  // Validates what is DISPLAYED, so the messages describe the address on screen.
  const address = displayedAddress.value;

  const partErrors = [
    validationRules.street(address.street, props.wasValidated) ? undefined : "Please enter a valid street name",
    validationRules.streetNumber(address.streetNumber, props.wasValidated) ? undefined : "Please enter a valid street number",
    validationRules.city(address.city, props.wasValidated) ? undefined : "Please enter a valid city name",
    validationRules.postalCode(address.postalCode, props.wasValidated) ? undefined : "Please enter a valid postal code",
    validationRules.country(address.country, props.wasValidated)
      ? undefined
      : `Please enter a country supported by the platform (${localization.getValidCountriesList().join(", ")})`,
  ].filter((error) => error);

  const hasNoStreetParts = !address.street && !address.streetNumber && !address.city && !address.postalCode;

  // Nothing entered at all, on a host that does not require an address: not an error. This is the normal
  // state of every project the start dialog creates — a country and no street — and `wasValidated` on the
  // settings page is `saveAttempted`, so without this the section turned red after a save that succeeded.
  if (hasNoStreetParts && !props.required) return [];

  // Part-by-part messages are right when Google returns a place that is missing one part: a rural address with
  // no street number, or a country the platform does not support. They are wrong when nothing has been entered
  // at all, because then all five fire at once and four of them name a field this step does not have — there is
  // one input, and the only thing to do about any of it is enter an address. So say that, once.
  //
  // No `&& !address.country` term, deliberately. That made this unreachable in the flow that needs it most:
  // a project created without an address — now the normal case, since the create wizard no longer asks for
  // one — arrives here with a country and no street, and the four street messages stacked under a single
  // input. Exactly what this guard exists to prevent, defeated by testing the one part that is never empty.
  if (hasNoStreetParts && partErrors.length > 0) return ["Please enter an address"];

  // A PARTIAL address is still worth complaining about even when the whole thing is optional: a place that
  // came back without a street number is a bad answer, not an absent one.
  return partErrors;
});

// Deliberately the LIVE country, not the displayed one: localization drives the data providers the
// whole wizard loads and the values Save writes, and a read-only preview must not repoint those.
watch(
  () => project?.value?.buildingAddress?.country,
  (newCountry) => {
    localization.setCountryFromLongName(newCountry);
  }
);

/**
 * Scope the address search to the country the project already has, whatever brought it here.
 *
 * Create used to be the exception, returning undefined. That was right while the country CAME FROM this
 * step — restricting the search to a country nobody had chosen yet would have been circular. It stopped
 * being right when the start-project dialog began settling the country up front, and it is what keeps
 * that dialog's promise that the country is fixed for the life of the project: the country follows the
 * address (see the watcher above), so an unscoped search returning a Swedish place for a Danish project
 * would silently replace the calculation type chosen in the dialog.
 *
 * Live since RTLCA-5812, having been inert for as long as it existed: `getCountryInAlpha2` looked up
 * `countryInAlpha2Map[country.toLowerCase()]` against a map keyed by the Country_Enum's UPPERCASE values,
 * so it returned undefined for every input and AddressInput skipped both restriction paths. It resolves
 * the long name through `countryLongNameToEnumMap` now.
 *
 * Still keyed on "does the project have a country", not on the mode, so a project whose country is empty
 * or outside the alpha-2 map searches everywhere exactly as before — the safe direction, and the reason
 * this can be turned on without stranding anyone.
 */
const restrictToCountry = computed(() => localization.getCountryInAlpha2(project.value.buildingAddress.country));

/**
 * The country whose regulation guidance is on screen — the DISPLAYED address's, not localization's.
 *
 * Localization deliberately follows the LIVE country (see the watcher above), because it drives the data
 * providers and the values Save writes and a read-only preview must not repoint those. The guidance card
 * is display-only, so it can afford to follow what is actually being shown: previewing a revision whose
 * address was in another country otherwise put that revision's address above today's country's rules.
 *
 * A plain map lookup rather than `setCountryFromLongName`, for the same reason — no side effect.
 */
const guidanceCountry = computed<Country_Enum | undefined>(() => countryLongNameToEnumMap[displayedAddress.value.country]);

/** Open on arrival: the guidance is reference material the reader may want, not a surprise to opt into. */
const isGuidanceOpen = ref(true);
</script>

<template>
  <div class="w-full">
    <StepIntro
      class="mb-6"
      heading="Where is the building?"
      hint="The address places the project, and its country decides which sections these settings hold and which emission data is used." />

    <!-- One column of cards, like every other section on the settings page. It also replaces a spacing
         contract that had quietly expired: the guidance below used to take no margin of its own because
         it was "a direct child of the wizard's space-y-12 column", and no such column exists any more —
         SettingsSection adds nothing, so the two would have butted together at 0px. Keeping both inside
         one root matters for a second reason: the page hands every section an @name-taken listener, and
         a multi-root component cannot inherit it. -->
    <div class="flex w-full flex-col gap-3">
      <StepCard
        :icon="MapPin"
        heading="Address"
        caption="Start typing and pick the building from the suggestions. The search is limited to the project's country, and the map below confirms what was found.">
        <!-- A native disabled fieldset makes the address field inert while a revision's saved address is
             on screen, so the autocomplete cannot rewrite a value this step is not allowed to save. -->
        <fieldset
          class="grid grid-cols-12 gap-x-4 gap-y-6"
          :disabled="isReadOnly">
          <AddressInput
            v-model:value="displayedAddress"
            class="col-span-12"
            label="Address"
            color="primary"
            :required="required"
            :restrict-to-country="restrictToCountry"
            :error-msg="addressInputErrors"
            map-placeholder="The map will confirm the address you enter" />
        </fieldset>
      </StepCard>

      <!-- The country's regulation guidance, in the same panel grammar as the B6 one — the page's
           established shell for long-form reference material, as opposed to the step cards that hold
           fields. It is reading matter rather than a form: for some countries it runs to eight
           paragraphs and seven links, so it collapses, but it opens expanded because it is guidance the
           reader may well have come for.

           Below the address rather than above it, unlike B6's: it only appears once an address implies a
           country, so it arrives in the order it is caused. It lands before the map does — the country is
           known as soon as the address is parsed, while the map still has to geocode and draw. -->
      <!-- Faded in, because it arrives as a consequence of the address rather than being part of the
           form: appearing between two frames reads, for eight paragraphs of regulation, as the page
           having changed under you. -->
      <Transition name="fade-in">
        <div v-if="guidanceCountry || displayedAddress.country">
          <button
            type="button"
            class="rounded-8 border-app-blue-200 bg-app-blue-50 hover:bg-app-blue-100 duration-fast ease-standard flex w-full items-center gap-2 border px-4 py-3 text-left transition-colors"
            :class="{ 'rounded-b-none border-b-0': isGuidanceOpen }"
            :aria-expanded="isGuidanceOpen"
            aria-controls="location-regulation-guidance"
            @click="isGuidanceOpen = !isGuidanceOpen">
            <BookOpen
              class="icon-sm text-app-blue-500 shrink-0"
              :stroke-width="1.5"
              aria-hidden="true" />
            <span class="t-small text-app-blue-900 flex-1 font-semibold">Regulation and calculation method</span>
            <component
              :is="isGuidanceOpen ? ChevronUp : ChevronDown"
              class="icon-xs text-app-blue-400 shrink-0"
              :stroke-width="1.5"
              aria-hidden="true" />
          </button>

          <div
            v-if="isGuidanceOpen"
            id="location-regulation-guidance"
            class="rounded-b-8 border-app-blue-200 bg-app-blue-50 flex flex-col gap-4 border border-t-0 px-4 pt-3 pb-4">
            <DK v-if="guidanceCountry === Country_Enum.DENMARK" />
            <DE v-else-if="guidanceCountry === Country_Enum.GERMANY" />
            <GL v-else-if="guidanceCountry === Country_Enum.GREENLAND" />
            <NO v-else-if="guidanceCountry === Country_Enum.NORWAY" />
            <SE v-else-if="guidanceCountry === Country_Enum.SWEDEN" />
            <FI v-else-if="guidanceCountry === Country_Enum.FINLAND" />
            <IS v-else-if="guidanceCountry === Country_Enum.ICELAND" />
            <FO v-else-if="guidanceCountry === Country_Enum.FAROE_ISLAND" />
            <IE v-else-if="guidanceCountry === Country_Enum.IRELAND" />
            <Fallback v-else />
          </div>
        </div>
      </Transition>
    </div>
  </div>
</template>
